/**
 * Public platform — page rhythm (Home first, then inner public pages).
 *
 * Core vertical rhythm — fixed rem scale only (no responsive spacing):
 * - Space from header: 9rem · pt-36
 * - Between sections: 8rem · mt-32
 * - After section title: 3rem · mt-12
 * - Section title to content (compact): 2rem · mt-8
 * - End-of-section CTA top: 6rem · mt-24
 * - Footer top: 9rem · pt-36
 * - Footer bottom: 3rem · pb-12
 */

/**
 * Fixed rem values — source of truth for public page spacing.
 * `axis`: Tailwind margin/padding direction (mt | mb | pt | pb).
 * `className`: ready-to-use utility class.
 */
export const PUBLIC_SPACING = {
  headerOffset: { rem: '9rem', axis: 'pt', className: 'pt-36' },
  sectionGap: { rem: '8rem', axis: 'mt', className: 'mt-32' },
  headingToContent: { rem: '3rem', axis: 'mt', className: 'mt-12' },
  headingToContentCompact: { rem: '2rem', axis: 'mt', className: 'mt-8' },
  contentToCta: { rem: '6rem', axis: 'mt', className: 'mt-24' },
  footerTop: { rem: '9rem', axis: 'pt', className: '!pt-36' },
  footerBottom: { rem: '3rem', axis: 'pb', className: 'pb-12' },
};

/** Space from header to page content — 9rem · pt-36. */
export const PUBLIC_MAIN_HEADER_OFFSET_CLASS = PUBLIC_SPACING.headerOffset.className;

/** Section title to content below (compact) — 2rem · mt-8. FAQs, help blocks, etc. */
export const PUBLIC_HEADING_TO_CONTENT_COMPACT_CLASS =
  PUBLIC_SPACING.headingToContentCompact.className;

/** Between sections — 8rem · mt-32. */
export const PUBLIC_SECTION_GAP_CLASS = PUBLIC_SPACING.sectionGap.className;

/** @deprecated Use PUBLIC_SECTION_GAP_CLASS — same value. */
export const PUBLIC_SECTION_GAP_HEAVY_CLASS = PUBLIC_SECTION_GAP_CLASS;

/** Section heading (H2) to content below — 3rem · mt-12. */
export const PUBLIC_HEADING_TO_CONTENT_CLASS = PUBLIC_SPACING.headingToContent.className;

/** Cards / content to end-of-section CTA row — 6rem · mt-24. */
export const PUBLIC_CONTENT_TO_CTA_CLASS = PUBLIC_SPACING.contentToCta.className;

/** Footer top spacing before footer block — 9rem · pt-36. */
export const PUBLIC_FOOTER_TOP_CLASS = PUBLIC_SPACING.footerTop.className;

/** Footer outer bottom spacing — 3rem · pb-12. */
export const PUBLIC_FOOTER_OUTER_BOTTOM_CLASS = PUBLIC_SPACING.footerBottom.className;

/** Section top padding — 8rem · pt-32 (alias of sectionGap). */
export const PUBLIC_SECTION_PADDING_TOP_CLASS = 'pt-32';

/** Section bottom padding — 8rem · pb-32 (alias of sectionGap). */
export const PUBLIC_SECTION_PADDING_BOTTOM_CLASS = 'pb-32';

/** @deprecated Use PUBLIC_SECTION_PADDING_TOP_CLASS — same value. */
export const PUBLIC_SECTION_PADDING_VISUAL_TOP_CLASS = PUBLIC_SECTION_PADDING_TOP_CLASS;

/** Hero / full-bleed block to first content section — 8rem · mb-32. */
export const PUBLIC_HERO_TO_SECTION_CLASS = 'mb-32';

/** Gap from full-viewport hero to first section — 8rem · pt-32. */
export const PUBLIC_HOME_HERO_TO_SECTION_CLASS = 'pt-32';

/** About page section bottom break — 8rem · pb-32. */
export const PUBLIC_ABOUT_SECTION_BOTTOM_CLASS = 'pb-32';

/** Policy / article major section gap — 8rem · mt-32. */
export const PUBLIC_ARTICLE_SECTION_GAP_CLASS = PUBLIC_SECTION_GAP_CLASS;

/** Public site header bar height — matches Header `h-[80px]`. */
export const PUBLIC_MAIN_HEADER_HEIGHT_CLASS = 'pt-20';

/** Gap below public header before page content — legacy; prefer PUBLIC_MAIN_HEADER_OFFSET_CLASS. */
export const PUBLIC_MAIN_HEADER_CONTENT_GAP_CLASS = 'pt-12';

function joinPublicLayoutClasses(base, extraClasses = '') {
  const extra = (extraClasses || '').trim();
  return extra ? `${base} ${extra}` : base;
}

/** Resolve a core PUBLIC_SPACING token to its Tailwind class, with optional extras. */
export function publicSpacingClass(spacingKey, extraClasses = '') {
  return joinPublicLayoutClasses(PUBLIC_SPACING[spacingKey].className, extraClasses);
}

/** Merge public header offset with optional extra classes. */
export function publicMainHeaderOffsetClass(extraClasses = '') {
  return joinPublicLayoutClasses(PUBLIC_MAIN_HEADER_OFFSET_CLASS, extraClasses);
}

/** Between sections — 8rem · mt-32. */
export function publicSectionGapClass(extraClasses = '') {
  return publicSpacingClass('sectionGap', extraClasses);
}

/** After section title to content — 3rem · mt-12. */
export function publicHeadingToContentClass(extraClasses = '') {
  return publicSpacingClass('headingToContent', extraClasses);
}

/** Content to end-of-section CTA — 6rem · mt-24. */
export function publicContentToCtaClass(extraClasses = '') {
  return publicSpacingClass('contentToCta', extraClasses);
}

/** Space from header to page content — 9rem · pt-36. */
export function publicHeaderOffsetClass(extraClasses = '') {
  return publicSpacingClass('headerOffset', extraClasses);
}

/** Section title to content below (compact) — 2rem · mt-8. */
export function publicHeadingToContentCompactClass(extraClasses = '') {
  return publicSpacingClass('headingToContentCompact', extraClasses);
}

/** Top offset for inner public pages under the absolute header (alias). */
export const PUBLIC_PAGE_TOP_UNDER_HEADER_CLASS = PUBLIC_MAIN_HEADER_OFFSET_CLASS;

/** Subsection heading block (title + divider). */
export const PUBLIC_SUBSECTION_HEADING_CLASS =
  'mb-5 border-b border-b-mediumgray pb-4';

/** FAQ / help link stack. */
export const PUBLIC_HELP_LINK_STACK_CLASS = 'mb-2';

/** FAQ list item gap. */
export const PUBLIC_FAQ_ITEM_GAP_CLASS = 'mt-10';

/** Help Centre category index grid — columns only; pair with PUBLIC_CARD_GRID_GAP_CLASS. */
export const PUBLIC_HELP_CATEGORY_GRID_CLASS = 'grid grid-cols-1 sm:grid-cols-2';

/** Paths that use public platform footer spacing. */
export const isPublicFooterSpacingPath = (pathname) =>
  pathname === '/' ||
  pathname.startsWith('/HelpCentre') ||
  pathname === '/contactus' ||
  pathname === '/about-us' ||
  pathname === '/blogs' ||
  pathname.startsWith('/blogs/') ||
  pathname === '/privacy-policy' ||
  pathname === '/terms-&-conditions' ||
  pathname === '/pricing' ||
  pathname === '/pro' ||
  pathname.startsWith('/services-guide') ||
  pathname === '/customer-support-centre' ||
  pathname === '/business-support-centre' ||
  pathname === '/category' ||
  pathname.startsWith('/category/') ||
  pathname === '/service-detail' ||
  pathname.startsWith('/service-detail/') ||
  pathname === '/poster-profile' ||
  pathname.startsWith('/poster-profile/');

/** About page multi-column layout gap. */
export const PUBLIC_ABOUT_COLUMN_GAP_CLASS = 'gap-12';

/** Form field to field. Field internal sizing unchanged. */
export const PUBLIC_FORM_FIELD_GAP_CLASS = 'mt-5';

/** Form content to submit / action row. */
export const PUBLIC_FORM_TO_ACTION_CLASS = 'mt-8';

/** Article subsection heading to body. */
export const PUBLIC_ARTICLE_SUBSECTION_GAP_CLASS = 'mt-5';

/** Article body paragraph gap. */
export const PUBLIC_ARTICLE_PARAGRAPH_GAP_CLASS = 'mt-4';

/** Services guide listing — page title to layout — 4rem · mt-16. */
export const PUBLIC_SERVICES_GUIDE_TITLE_GAP_CLASS = 'mt-16';

/** Help Centre — title/search to next block — 4rem · mt-16. */
export const PUBLIC_HELP_CENTRE_BLOCK_GAP_CLASS = 'mt-16';

/** Blog listing — page title to layout — 4rem · mt-16. */
export const PUBLIC_BLOG_LISTING_TITLE_GAP_CLASS = 'mt-16';

/** Category browse — map / content to filter bar, filter bar to listings — 4rem · mt-16. */
export const PUBLIC_CATEGORY_FILTER_GAP_CLASS = 'mt-16';

/** Full-width divider — vertical spacing before and after — 4rem · mt-16. */
export const PUBLIC_DIVIDER_VERTICAL_GAP_CLASS = 'mt-16';

/** Tab bar to panel content — 4rem · mt-16. */
export const PUBLIC_TAB_TO_CONTENT_GAP_CLASS = 'mt-16';

/** Profile reviews — stats card to review list — 4rem · mt-16. */
export const PUBLIC_PROFILE_REVIEW_STATS_TO_LIST_CLASS = 'mt-16';

/** Blog listing two-column layout gap. */
export const PUBLIC_BLOG_LAYOUT_COLUMN_GAP_CLASS = 'gap-16';

/** Blog list item divider — 3rem above and below rule · mt-12 pt-12. */
export const PUBLIC_BLOG_LIST_DIVIDER_CLASS =
  'mt-12 pt-12 border-t border-t-mediumgray';

/** About page hero top offset under absolute header. */
export const PUBLIC_ABOUT_BANNER_TOP_CLASS = PUBLIC_MAIN_HEADER_OFFSET_CLASS;

/** Card grid gap. */
export const PUBLIC_CARD_GRID_GAP_CLASS = 'gap-6';

/** Home blog / preview card grid. */
export const PUBLIC_HOME_BLOG_GRID_CLASS =
  'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

/** Home category icon grid — columns only; pair with PUBLIC_HOME_CATEGORY_GRID_GAP_CLASS. */
export const PUBLIC_HOME_CATEGORY_GRID_CLASS =
  'grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 xl:grid-cols-6 w-full';

/** Home category icon grid gap — horizontal 1.5rem, vertical 3rem · gap-x-6 gap-y-12. */
export const PUBLIC_HOME_CATEGORY_GRID_GAP_CLASS = 'gap-x-6 gap-y-12';

/** Browse slider item horizontal inset. */
export const PUBLIC_BROWSE_SLIDER_ITEM_GAP_CLASS = 'mx-3';

/** Image to caption under browse slider items. */
export const PUBLIC_SLIDER_IMAGE_TO_LABEL_CLASS = 'mt-2';

/** Icon / circle to label text — 0.75rem (12px) · mt-3. */
export const PUBLIC_ICON_TO_TEXT_CLASS = 'mt-3';

/** Card image to title. */
export const PUBLIC_IMAGE_TO_HEADING_CLASS = 'mt-5';

/** Title to body / intro paragraph. */
export const PUBLIC_HEADING_TO_BODY_CLASS = 'mt-4';

/** Body text block to link / inline CTA. */
export const PUBLIC_BODY_TO_LINK_CLASS = 'mt-8';

/** Heading row to inline action (e.g. H2 + button). */
export const PUBLIC_HEADING_ROW_GAP_CLASS = 'gap-5';

/** Story / scroll content blocks. */
export const PUBLIC_STORY_POINTS_GAP_CLASS = 'space-y-16';

/** Custom marker to text block. */
export const PUBLIC_STORY_MARKER_GAP_CLASS = 'gap-4';

/** Swiper / preview card outer margin. */
export const PUBLIC_SWIPER_CARD_MARGIN_CLASS = 'm-3';

/** Card overlay inset padding. */
export const PUBLIC_CARD_OVERLAY_PADDING_CLASS = 'p-6';

/** Avatar to title in overlay row. */
export const PUBLIC_SWIPER_AVATAR_ROW_GAP_CLASS = 'gap-2';

/** Post detail profile avatar — standard tier (--avatar-standard-size, responsive). */
export const PUBLIC_POST_DETAIL_PROFILE_AVATAR_CLASS =
  'avatar-standard object-cover object-center';

/** Avatar + PRO badge wrapper — fixed to --avatar-standard-size. */
export const PUBLIC_POST_DETAIL_PROFILE_AVATAR_WRAPPER_CLASS =
  'post-detail-profile-avatar-wrap';

/** PRO badge — straddles bottom of avatar (same offset as title-row PRO). */
export const PUBLIC_POST_DETAIL_PROFILE_PRO_BADGE_CLASS =
  'post-detail-profile-pro-badge';

/** Post detail divider notch — scales with --avatar-standard-size (+ 3rem inset). */
export const PUBLIC_POST_DETAIL_PROFILE_NOTCH_CLASS =
  'post-detail-profile-notch flex shrink-0 items-center justify-center';

/** Footer inner panel padding. */
export const PUBLIC_FOOTER_INNER_PANEL_CLASS = 'pt-16 px-8 pb-10';

/** Footer column gap. */
export const PUBLIC_FOOTER_COLUMN_GAP_CLASS = 'gap-10';

/** Footer section heading to links. */
export const PUBLIC_FOOTER_HEADING_TO_LINKS_CLASS = 'mt-4';

/** Footer link stack gap. */
export const PUBLIC_FOOTER_LINK_GAP_CLASS = 'mt-2';

/** Footer props for public platform pages (Home passes these directly). */
export const PUBLIC_FOOTER_SPACING_PROPS = {
  className: PUBLIC_FOOTER_TOP_CLASS,
  outerBottomClassName: PUBLIC_FOOTER_OUTER_BOTTOM_CLASS,
  innerPanelPaddingClassName: PUBLIC_FOOTER_INNER_PANEL_CLASS,
  columnsGapClassName: PUBLIC_FOOTER_COLUMN_GAP_CLASS,
  listTopClassName: PUBLIC_FOOTER_HEADING_TO_LINKS_CLASS,
  linkItemClassName: PUBLIC_FOOTER_LINK_GAP_CLASS,
};
